import {
  getCurrentScope,
  onScopeDispose,
  ref,
  watch,
  type Ref,
  type WatchSource,
} from "vue";

// One value per organization, shared by every component that shows it.
export type OrgCacheEntry<T> = {
  data: Ref<T | null>;
  loading: Ref<boolean>;
  error: Ref<unknown>;
  // components currently showing this organization's value
  users: number;
  // only the latest request may write: older answers (e.g. fetched with the
  // previous token, or before a mutation) are dropped
  seq: number;
};

// A per-organization cache (plan section 4.3.4). `invalidate` refetches the
// entries that are on screen, keeping their current value until the new one
// arrives, and drops the others, so a token refresh or an organization change
// never leaves a banner or page empty.
export function createOrgCache<T>(load: (orgId: string) => Promise<T>) {
  const entries = new Map<string, OrgCacheEntry<T>>();

  const entryFor = (orgId: string): OrgCacheEntry<T> => {
    let entry = entries.get(orgId);
    if (!entry) {
      entry = {
        data: ref(null) as Ref<T | null>,
        loading: ref(false),
        error: ref(null),
        users: 0,
        seq: 0,
      };
      entries.set(orgId, entry);
    }
    return entry;
  };

  const fetch = async (orgId: string): Promise<void> => {
    const entry = entryFor(orgId);
    const seq = ++entry.seq;
    entry.loading.value = true;
    entry.error.value = null;
    try {
      const value = await load(orgId);
      if (seq === entry.seq) entry.data.value = value;
    } catch (err) {
      if (seq === entry.seq) {
        entry.error.value = err;
        entry.data.value = null;
      }
    } finally {
      if (seq === entry.seq) entry.loading.value = false;
    }
  };

  // `refetch: false` only drops the values, e.g. when the session just
  // became restricted and nothing else may be fetched.
  const invalidate = (orgId?: string, { refetch = true } = {}) => {
    const ids = orgId ? [orgId] : [...entries.keys()];
    for (const id of ids) {
      const entry = entries.get(id);
      if (!entry) continue;
      if (refetch && entry.users > 0) {
        void fetch(id);
      } else {
        entry.seq++;
        entry.data.value = null;
        entry.error.value = null;
        entry.loading.value = false;
      }
    }
  };

  // Counts the calling component as a user of the organization `orgId`
  // points at, for as long as its scope lives.
  const retain = (orgId: WatchSource<string | null | undefined>) => {
    let current: string | null = null;
    const release = () => {
      if (current) entryFor(current).users--;
      current = null;
    };
    watch(
      orgId,
      (id) => {
        release();
        if (id) {
          entryFor(id).users++;
          current = id;
        }
      },
      { immediate: true },
    );
    if (getCurrentScope()) onScopeDispose(release);
  };

  return { entryFor, fetch, invalidate, retain };
}
